import { useEffect, useMemo, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { Card, Button, Pill } from "@bios/ui";
import {
  dataSourcesFor,
  getModule,
  seedSourcesFor,
  type DataSourceSpec,
  type SeedSourceSpec,
} from "@bios/taxonomies";
import { useApiAuthed, useApiDev } from "../auth.js";

interface PageProps {
  devModeNoAuth?: boolean;
}

type RegisterStatus = "pending" | "existed" | "created" | "failed";
type TriggerStatus = "pending" | "queued" | "failed" | "skipped";

interface Row {
  spec: SeedSourceSpec;
  source_id?: string;
  register: RegisterStatus;
  trigger: TriggerStatus;
  error?: string;
}

export function ModuleSeed({ devModeNoAuth }: PageProps) {
  const { module: moduleParam = "" } = useParams<{ module: string }>();
  const moduleConfig = useMemo(() => getModule(moduleParam), [moduleParam]);
  const sources = useMemo<readonly SeedSourceSpec[]>(
    () => seedSourcesFor(moduleParam),
    [moduleParam],
  );
  const dataSources = useMemo<readonly DataSourceSpec[]>(
    () => dataSourcesFor(moduleParam),
    [moduleParam],
  );

  const api = devModeNoAuth ? useApiDev() : useApiAuthed();
  const [rows, setRows] = useState<Row[]>(() =>
    sources.map((spec) => ({ spec, register: "pending", trigger: "pending" })),
  );
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);
  const [meRole, setMeRole] = useState<string | null>(null);
  const [meError, setMeError] = useState<string | null>(null);

  // If the URL module is unknown, bounce.
  if (!moduleConfig) {
    return <Navigate to="/app" replace />;
  }

  // Reset rows when navigating between modules.
  useEffect(() => {
    setRows(sources.map((spec) => ({ spec, register: "pending", trigger: "pending" })));
    setDone(false);
    setRunning(false);
  }, [sources]);

  useEffect(() => {
    let cancelled = false;
    api
      .me()
      .then((r) => {
        if (!cancelled) setMeRole(r.user.role);
      })
      .catch((e) => !cancelled && setMeError(String(e)));
    return () => {
      cancelled = true;
    };
  }, [api]);

  async function run() {
    if (sources.length === 0) return;
    setRunning(true);
    setDone(false);

    const next = rows.map((r) => ({ ...r }));

    let existingByUrl = new Map<string, string>();
    try {
      const existing = await api.sources(moduleParam);
      existingByUrl = new Map(existing.sources.map((s) => [s.url, s.id]));
    } catch (err) {
      next.forEach((r) => {
        r.register = "failed";
        r.trigger = "skipped";
        r.error = `prefetch_failed: ${err}`;
      });
      setRows(next);
      setRunning(false);
      setDone(true);
      return;
    }

    for (let i = 0; i < sources.length; i += 1) {
      const r = next[i];
      if (!r) continue;
      const spec = r.spec;
      const existingId = existingByUrl.get(spec.url);
      if (existingId) {
        r.source_id = existingId;
        r.register = "existed";
        setRows([...next]);
        continue;
      }
      try {
        const res = await api.createSource({
          name: spec.name,
          url: spec.url,
          module: spec.module,
          type: spec.type,
          tier: spec.tier,
          trust_weight: spec.trust_weight,
          active: 1,
        });
        r.source_id = res.source.id;
        r.register = "created";
      } catch (err) {
        r.register = "failed";
        r.error = String(err);
      }
      setRows([...next]);
    }

    for (let i = 0; i < sources.length; i += 1) {
      const r = next[i];
      if (!r) continue;
      if (!r.source_id) {
        r.trigger = "skipped";
        setRows([...next]);
        continue;
      }
      try {
        await api.triggerSource(r.source_id);
        r.trigger = "queued";
      } catch (err) {
        r.trigger = "failed";
        r.error = (r.error ? `${r.error} | ` : "") + String(err);
      }
      setRows([...next]);
    }

    setRunning(false);
    setDone(true);
  }

  const allDoneOk =
    done && rows.every((r) => r.register !== "failed" && r.trigger === "queued");
  const someFailed =
    done && rows.some((r) => r.register === "failed" || r.trigger === "failed");

  return (
    <>
      <div className="bios-page-header">
        <div>
          <h1 className="bios-page-title">Bootstrap: {moduleConfig.label}</h1>
          <p className="bios-page-sub">
            Admin-only one-shot tool. Registers curated {moduleParam} signal sources and
            triggers ingestion on each. Baseline data sources are shown for validation only.
          </p>
        </div>
      </div>

      <Card>
        <div className="bios-seed-controls">
          <div className="bios-seed-meta">
            <div>
              Module:&nbsp;
              <strong>{moduleConfig.label}</strong> ({moduleConfig.key})
            </div>
            <div>
              Logged-in role:&nbsp;
              <strong
                style={{ color: meRole === "admin" ? "var(--bios-signal)" : "var(--bios-noise)" }}
              >
                {meRole ?? "loading…"}
              </strong>
            </div>
            {meRole && meRole !== "admin" && (
              <div style={{ fontSize: 12, color: "var(--bios-noise)" }}>
                Source POSTs require admin. /v1/sources will return 403 for this account.
              </div>
            )}
            {meError && <div style={{ fontSize: 12, color: "var(--bios-noise)" }}>{meError}</div>}
            {sources.length === 0 && (
              <div style={{ fontSize: 12, color: "var(--bios-noise)" }}>
                No seed sources defined for module &quot;{moduleParam}&quot;. Add entries to
                <code> packages/taxonomies/src/seedSources.ts </code>.
              </div>
            )}
          </div>
          <Button
            variant="primary"
            onClick={run}
            disabled={running || meRole !== "admin" || sources.length === 0}
            style={{ minHeight: 44, padding: "10px 18px" }}
          >
            {running
              ? "Running…"
              : done
              ? someFailed
                ? "Retry failed"
                : "Run again"
              : `Register all ${sources.length} sources`}
          </Button>
        </div>
      </Card>

      <div className="bios-seed-list">
        {rows.map((r) => (
          <Card key={r.spec.url}>
            <div className="bios-seed-row">
              <div className="bios-seed-row-head">
                <div className="bios-seed-row-name">{r.spec.name}</div>
                <Pill>
                  {r.spec.tier} · {r.spec.type}
                </Pill>
              </div>
              <div className="bios-seed-row-url">{r.spec.url}</div>
              <div className="bios-seed-row-statuses">
                <StatusChip label="Register" value={r.register} />
                <StatusChip label="Trigger" value={r.trigger} />
                {r.source_id && (
                  <span className="bios-seed-row-id">id: {r.source_id.slice(0, 8)}…</span>
                )}
              </div>
              {r.error && <div className="bios-seed-row-error">{r.error}</div>}
            </div>
          </Card>
        ))}
      </div>

      {allDoneOk && (
        <Card>
          <p style={{ margin: 0, color: "var(--bios-signal)", fontWeight: 500 }}>
            All {sources.length} sources queued for ingestion. Wait ~1–3 minutes for items to
            land, then check the {moduleConfig.label} module dashboard.
          </p>
        </Card>
      )}

      {dataSources.length > 0 && (
        <Card title="Baseline data sources">
          <div style={{ display: "grid", gap: 12 }}>
            <p style={{ margin: 0, color: "var(--bios-text-muted)", fontSize: 13 }}>
              These sources are not registered or ingested as events. Use them for validation,
              aggregate baseline metrics, and cross-checking workforce signals.
            </p>
            {dataSources.map((spec) => (
              <div key={spec.url} className="bios-seed-row">
                <div className="bios-seed-row-head">
                  <div className="bios-seed-row-name">{spec.name}</div>
                  <Pill>
                    {spec.tier} · {spec.type}
                  </Pill>
                </div>
                <div className="bios-seed-row-url">{spec.url}</div>
                <div className="bios-seed-row-statuses">
                  <StatusChip label="Register" value="skipped" />
                  <StatusChip label="Trigger" value="skipped" />
                  <span className="bios-seed-row-id">
                    metrics: {spec.metrics.join(", ")} · {spec.use.replace(/_/g, " ")}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </>
  );
}

function StatusChip({
  label,
  value,
}: {
  label: string;
  value: RegisterStatus | TriggerStatus;
}) {
  const color =
    value === "created" || value === "queued"
      ? "var(--bios-signal)"
      : value === "existed"
      ? "var(--bios-info)"
      : value === "failed"
      ? "var(--bios-noise)"
      : value === "skipped"
      ? "var(--bios-text-faint)"
      : "var(--bios-text-muted)";
  return (
    <span className="bios-seed-chip" style={{ borderColor: color, color }}>
      <span className="bios-seed-chip-label">{label}</span>
      <span className="bios-seed-chip-value">{value}</span>
    </span>
  );
}
